import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import {
  ExtendedParametersTabsEnum,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsUseCasesStore,
} from '@store';
import { GTMService, UIService } from '@services';
import { scrollToBottom } from '@common';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DiagnosticsChartService } from '../diagnostics-chart.service';
import { DiagnosticsVddSelectionModalComponent } from '../diagnostics-vdd-selection-modal/diagnostics-vdd-selection-modal.component';
import { DiagnosticsUseCasesModalComponent } from '../diagnostics-use-cases-modal/diagnostics-use-cases-modal.component';

@Component({
  selector: 'app-diagnostics-management-tab',
  templateUrl: './diagnostics-management-tab.component.html',
  styleUrls: ['./diagnostics-management-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsManagementTabComponent implements OnInit, OnDestroy {
  ExtendedParametersTabsEnum = ExtendedParametersTabsEnum;
  selectedParameters: RemoteDiagnosticsParameter[];
  selectedOrStartedParameters: RemoteDiagnosticsParameter[];
  isAnyParameterRunningAndSelected: boolean;
  disableGoToInfographicButton: boolean;
  disableClearAllButton: boolean;
  gatewaySerial: string;
  showPopover = false;
  selectAll: boolean;
  interval: number;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public remoteDiagnosticsUseCasesStore: RemoteDiagnosticsUseCasesStore,
    public gtmService: GTMService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private uiService: UIService,
    private diagnosticsChartService: DiagnosticsChartService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];

    this.initSubscriptions();
  }

  onSelectAllChange(event: MatCheckboxChange): void {
    this.interval = undefined;
    if (event.checked) {
      this.remoteDiagnosticsStore.selectAllParameters(this.gatewaySerial);
    } else {
      this.remoteDiagnosticsStore.unselectAllParameters(this.gatewaySerial);
    }
  }

  onIntervalChange(event: MatSelectChange): void {
    this.remoteDiagnosticsStore.changeIntervalAllParameters(event.value, this.gatewaySerial);
  }

  openModal(currentTab: ExtendedParametersTabsEnum): void {
    switch (currentTab) {
      case ExtendedParametersTabsEnum.SearchedParameters:
        this.dialog.open(DiagnosticsVddSelectionModalComponent, {
          width: '424px',
          disableClose: true,
        });
        break;
      case ExtendedParametersTabsEnum.UseCases:
        this.dialog.open(DiagnosticsUseCasesModalComponent, {
          width: '424px',
        });
        break;
    }
  }

  startAllSelected(): void {
    this.remoteDiagnosticsStore.scheduleParametersReadCycleAttempt(
      this.gatewaySerial,
      this.interval
    );
    this.uiService.isAnyParameterRunning$.next(true);
  }

  stopAllSelected(): void {
    this.remoteDiagnosticsStore.cancelMultipleParametersReadCycle(this.gatewaySerial, true);
  }

  clearSelectedSeries(): void {
    this.clearRunningParametersFromChart();
    this.removeStoppedParametersFromChart();
  }

  scrollToGraph(): void {
    const overlayContainer = document.getElementsByClassName('overlay-container')[0];
    scrollToBottom(overlayContainer);
  }

  deleteAllSelected(): void {
    if (this.isAnyParameterRunningAndSelected) {
      this.remoteDiagnosticsStore.cancelMultipleParametersReadCycle(this.gatewaySerial, true);
      this.remoteDiagnosticsStore.cancelMultipleParametersReadCycleInProgress$
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((isCancelAllInProgress) => {
          if (!isCancelAllInProgress) {
            this.remoteDiagnosticsStore.deleteSelectedParameters(this.gatewaySerial);
            this.removeStoppedParametersFromChart();
          }
        });
    } else {
      this.remoteDiagnosticsStore.deleteSelectedParameters(this.gatewaySerial);
      this.removeStoppedParametersFromChart();
    }
    this.gtmService.track('onConfirm', 'Delete all selected parameters', 'diagnosticsPage');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initSubscriptions(): void {
    this.remoteDiagnosticsStore
      .isAnyParameterRunningAndSelected(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isAnyParameterRunningAndSelected) => {
        this.isAnyParameterRunningAndSelected = isAnyParameterRunningAndSelected;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .getParametersStartedOrSelected(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedOrStartedParameters) => {
        this.selectedOrStartedParameters = selectedOrStartedParameters;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .getParametersSelectedOnly(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedParameters) => {
        this.selectedParameters = selectedParameters;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .isAnyParameterNotSelected(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isAnyParameterNotSelected) => {
        this.selectAll = !isAnyParameterNotSelected;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsUseCasesStore
      .getSelectedExtendedParametersTab(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.interval = undefined;
        this.cdr.detectChanges();
      });

    combineLatest([
      this.remoteDiagnosticsStore
        .getParametersSelectedOnly(this.gatewaySerial)
        .pipe(
          map((selectedParameters) =>
            selectedParameters.map((parameter) => parameter.vddDetail.name)
          )
        ),
      this.diagnosticsChartService.currentSeriesNames,
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([selectedParameterNames, seriesNames]) => {
        this.disableClearAllButton = !selectedParameterNames.some((selectedParameterName) =>
          seriesNames.includes(selectedParameterName)
        );
        this.cdr.detectChanges();
      });

    combineLatest([
      this.remoteDiagnosticsStore.isAnyParameterRunning(this.gatewaySerial),
      this.diagnosticsChartService.currentSeriesNames,
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([isAnyParameterRunning, seriesNames]) => {
        this.disableGoToInfographicButton = !isAnyParameterRunning && seriesNames.length === 0;
        this.cdr.detectChanges();
      });
  }

  private clearRunningParametersFromChart(): void {
    const runningParameters = this.selectedParameters.filter(
      (parameter) => parameter.requestId !== ''
    );
    if (runningParameters.length > 0) {
      this.diagnosticsChartService.clearSeries(
        runningParameters.map((parameter) => ({
          vddName: parameter.vddDetail.name,
          llName: this.diagnosticsChartService.getLLName(parameter),
        }))
      );
    }
  }

  private removeStoppedParametersFromChart(): void {
    const stoppedParameters = this.selectedParameters.filter(
      (parameter) => parameter.requestId === ''
    );
    if (stoppedParameters.length > 0) {
      this.diagnosticsChartService.removeSeries(
        stoppedParameters.map((parameter) => ({
          vddName: parameter.vddDetail.name,
          llName: this.diagnosticsChartService.getLLName(parameter),
        }))
      );
    }
  }
}
